import React, { forwardRef, useId } from 'react';
import { Label } from '../ui/Typography';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

type SelectOption = { value: string | number; label: string };
type GroupedOption = { label: string; options: SelectOption[] };

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  options?: Array<SelectOption | GroupedOption>;
  placeholder?: string;
  children?: React.ReactNode;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, helperText, fullWidth = true, options, placeholder, className = '', id, children, ...props }, ref) => {
    const generatedId = useId();
    const selectId = id || generatedId;
    const baseClasses = `
      px-4 py-3 pr-10
      bg-white dark:bg-gray-800
      border rounded-lg
      text-gray-900 dark:text-gray-100
      appearance-none
      focus:ring-2
      focus:border-transparent
      transition-all
      duration-200
      cursor-pointer
      disabled:bg-gray-50 dark:disabled:bg-gray-900
      disabled:text-gray-500 dark:disabled:text-gray-400
      disabled:cursor-not-allowed
    `;

    const stateClasses = error
      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500';

    const widthClass = fullWidth ? 'w-full' : '';

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <Label htmlFor={selectId} className="mb-1">
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={`
              ${baseClasses}
              ${stateClasses}
              ${widthClass}
              ${className}
            `}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {children || (options && options.map((option, index) => {
              if ('options' in option) {
                // It's a grouped option
                return (
                  <optgroup key={index} label={option.label}>
                    {option.options.map((subOption) => (
                      <option key={subOption.value} value={subOption.value}>
                        {subOption.label}
                      </option>
                    ))}
                  </optgroup>
                );
              } else {
                // It's a regular option
                return (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                );
              }
            }))}
          </select>
          <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        </div>
        {(error || helperText) && (
          <p className={`mt-1 text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
